import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SpreadsheetsState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetsState';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import bind from 'bind-decorator';
import {action, reaction} from 'mobx';

export class SpreadsheetsMainController
{
    @bind
    init()
    {
        reaction(() =>
            {
                const openDesign = commonControllers.designState.getOpenDesign();
                return openDesign && openDesign.designState;
            },
            () =>
            {
                const state = this._getSpreadsheetState();

                if( !state )
                    return;

                const workbook:Workbook = commonControllers.spreadsheets.main.getRootWorkbook();

                if( !commonControllers.spreadsheets.tabs.getActiveSheet(workbook.id) )
                    commonControllers.spreadsheets.tabs.setActiveSheet(workbook.id, workbook.runtime.sheets[0].id);
            });
    }

    @bind
    public getRootWorkbook():Workbook | null
    {
        const state:SpreadsheetsState | null = this._getSpreadsheetState();

        if( !state )
            return null;

        return state.rootWorkbook;
    }

    @bind
    public findWorkbookById(workbookId:WorkbookId):Workbook | null
    {
        const state:SpreadsheetsState | null = this._getSpreadsheetState();

        if( !state )
            return null;

        const result = this._getSpreadsheetState().workbooks.get(workbookId);

        if( !result )
            throw new Error(`Can't find workbook with id ${workbookId}`);

        return result;
    }

    public getAllWorkbookIds():string[]
    {
        const state = this._getSpreadsheetState();

        return state ? Array.from(state.workbooks.keys()) : [];
    }

    public findAnySheetById(sheetId:SheetId, includeFrameDetailsSheets:boolean = false):AnySheet | null
    {
        const state = this._getSpreadsheetState();

        if( !state )
            return null;

        const workbook:Workbook = state.sheetToWorkbookMap.get(sheetId) || null;

        if( !workbook )
            return null;

        const sheet:AnySheet = workbook.runtime.sheetsMap.get(sheetId) || null;

        if( !sheet && includeFrameDetailsSheets )
            console.warn('WARNING!!!! This case should be called only in WSP and method should be overriden in WspSpreadsheetsMainController');

        return sheet;
    }

    public getAllSheets():AnySheet[]
    {
        const {main} = commonControllers.spreadsheets;
        const workbookIds = main.getAllWorkbookIds();

        return workbookIds.reduce((acc, workbookId) =>
        {
            const workbook = main.findWorkbookById(workbookId);
            acc.push(...workbook.runtime.sheets);

            return acc;
        }, []);
    }

    public getAllSheetsMap():Map<SheetId, AnySheet>
    {
        const state = this._getSpreadsheetState();

        let result:Map<SheetId, AnySheet> = new Map();

        state.workbooks.forEach((workbook:Workbook) => (
            result = new Map([...Array.from(result.entries()), ...Array.from(workbook.runtime.sheetsMap.entries())])
        ));

        return result;
    }

    @action.bound
    public initializeRootWorkbook():void
    {
        if( this.getRootWorkbook() )
            this.getRootWorkbook().runtime.sheets
                .forEach((sheet:AnySheet) => commonControllers.spreadsheets.workbook.initializeSheet(sheet));
    }

    @action.bound
    public async terminateRootWorkbook():Promise<any>
    {
        if( this.getRootWorkbook() )
            return Promise.all(
                this.getRootWorkbook().runtime.sheets
                    .map((sheet:AnySheet) => commonControllers.spreadsheets.workbook.terminateSheet(sheet))
            );
    }

    @bind
    private static _newBlankWorkbook():Workbook
    {
        return Workbook.create();
    }

    @bind
    protected _getSpreadsheetState():SpreadsheetsState | null
    {
        const openDesign = commonControllers.designState.getOpenDesign();

        if( !openDesign || !openDesign.designState )
            return null;

        return openDesign.designState.spreadsheets;
    }
}
